"use client"

import { create } from "zustand"
import { usePathname, useSearchParams } from "next/navigation"
import { isGlobalAdsOff } from "@/lib/global-ads-off"

export type CornerAd = "sticky_banner" | "video_slider" | "in_page_push" | "instant_message"

// Corner widgets and the popunder are mounted once in app/layout.tsx, so
// they exist on every route. Where they are ALLOWED to appear is decided
// here, default-deny: a page has to be listed below to get one.
//
// Most of the app is one route ("/") switching views via ?view=, so a
// pathname check alone cannot tell the home Browse view from Gooncave. The
// two sub-page states that no URL reflects (the VR page's active tab and the
// Gooncave landing screen) are published by those pages into this store.
interface AdSurfaceState {
  vrBrowseTab: boolean
  gooncaveLanding: boolean
  setVRBrowseTab: (v: boolean) => void
  setGooncaveLanding: (v: boolean) => void
}

export const useAdSurfaceStore = create<AdSurfaceState>((set) => ({
  vrBrowseTab: false,
  gooncaveLanding: false,
  setVRBrowseTab: (vrBrowseTab) => set({ vrBrowseTab }),
  setGooncaveLanding: (gooncaveLanding) => set({ gooncaveLanding }),
}))

// Areas that never get any ad, of any kind.
const AD_FREE_PREFIXES = ["/goonpals", "/commissions", "/settings"]

export function isAdFreePath(pathname: string | null | undefined): boolean {
  // Sitewide Force Ads OFF (lib/global-ads-off.ts): no path gets ads.
  if (isGlobalAdsOff()) return true
  if (!pathname) return false
  return AD_FREE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))
}

/** True on the home page's plain Browse view (no ?view=). */
export function useIsHomeBrowseView(): boolean {
  const pathname = usePathname()
  const view = useSearchParams()?.get("view")
  return pathname === "/" && !view
}

/** Whether this corner widget may show on the page the user is on right now. */
export function useCornerAdPlacement(ad: CornerAd): boolean {
  const pathname = usePathname()
  const view = useSearchParams()?.get("view")
  const vrBrowseTab = useAdSurfaceStore((s) => s.vrBrowseTab)
  const gooncaveLanding = useAdSurfaceStore((s) => s.gooncaveLanding)

  if (isAdFreePath(pathname)) return false
  if (pathname === "/vr") return ad === "instant_message" && vrBrowseTab
  if (pathname === "/") {
    if (view === "gooncave") return gooncaveLanding && (ad === "sticky_banner" || ad === "in_page_push")
    if (view === "collections") return ad === "video_slider"
    return false
  }
  if (pathname?.startsWith("/goonfolio/")) return ad === "video_slider"
  return false
}
